import { SubscriptionProduct } from "@redotech/redo-billing-common/unified-billing/enums";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

/** Rates arrive as fractions (0.02 = 2%); the client formats. */
export const homeMetricFormats = ["currency", "percent", "count"] as const;

const HomeMetricSchema_ = z.object({
  /**
   * Identifies the metric within its product, e.g. "returnRate" under Returns.
   * Scoped rather than global, so a name only has to be unique where it means
   * something. Stable: it survives a change to the formula behind it.
   */
  type: z.string(),
  label: z.string(),
  format: z.enum(homeMetricFormats),
  value: z.number(),
  /** Same metric for the prior month, when that month has data. */
  previousValue: z.number().nullable(),
  /** A modelled proxy rather than a measurement, so the client can say so. */
  estimated: z.boolean(),
});

export const HomeMetricSchema: z.ZodType<HomeMetricSchema> = HomeMetricSchema_;
export interface HomeMetricSchema extends z.infer<typeof HomeMetricSchema_> {}

const HomeProductMetricsSchema_ = z.object({
  /** The Redo product that earns these figures, and what the group is billed as. */
  product: createEnum(SubscriptionProduct),
  /** Never empty: a product with nothing to show is left out entirely. */
  metrics: z.array(HomeMetricSchema),
});

export const HomeProductMetricsSchema: z.ZodType<HomeProductMetricsSchema> =
  HomeProductMetricsSchema_;
export interface HomeProductMetricsSchema extends z.infer<
  typeof HomeProductMetricsSchema_
> {}

export const getHomeMetrics = {
  name: "getHomeMetrics" as const,
  input: z.null(),
  output: z.object({
    /** The complete month the figures cover, e.g. "July 2026". */
    periodLabel: z.string(),
    /**
     * Grouped by product, so a figure is reached by product and then type
     * rather than by scanning one flat pool. Only products this merchant
     * actually has data for, in the order the registry declares them.
     */
    products: z.array(HomeProductMetricsSchema),
  }),
};
